"use client";

import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from "react";
import { useRouter } from "@/i18n/navigation";
import { authenticationApi, roleManagementApi, ELoginResponseStatus, LoginResponseUserBody, LoginResponseUserBodyFromJSONTyped } from "@/lib/api/apiwrapper";
import { roleHome } from "@/data/role-home";
import { ERoles } from "@/data/roles";
import { UserDto } from "@/lib/api/apiwrapper";
import { NEXT_API_URLS } from "@/utils/constants";
import { useLocale } from "next-intl";

type AuthContextType = {
  user: UserDto | null;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: ERoles) => boolean;
  isInRole: (role: ERoles) => boolean;
  switchRole: (roleId: string) => Promise<void>;
  refreshSession: () => Promise<void>;
};
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children, initialUser }: { children: ReactNode; initialUser: UserDto | null }) {
  const [user, setUser] = useState<UserDto | null>(initialUser);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const locale = useLocale();
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshSession();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Refresh periodically
    const intervalId = setInterval(() => {
      refreshSession();
    }, REFRESH_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [user]);

  const login = async (email: string, password: string, rememberMe: boolean) => {
    setIsLoading(true);
    try {
      const response = await authenticationApi.authenticationLoginPOST({ loginDto: { email: email, password: password, rememberMe: rememberMe } });
      if (response.status !== ELoginResponseStatus.Success || !response.body)
        throw new Error("Error during login");

      // Create nextjs session from response
      const sessionResponse = await fetch(NEXT_API_URLS.session, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(response.body),
      });

      if (!sessionResponse.ok) {
        throw new Error("Failed to create session");
      }

      const data = LoginResponseUserBodyFromJSONTyped(await sessionResponse.json(), false);
      setUser(data.user);

      const primaryRole = data.user?.currentRole.roleName as ERoles;

      router.push(roleHome[primaryRole]);
      router.refresh();
    } catch (error: any) {
      console.error("Login error");
      throw new Error(error.message || "Login failed");
    } finally {
      setIsLoading(false);

    }
  };

  const logout = async () => {
    try {
      await authenticationApi.authenticationLogoutPOST()
      await fetch(NEXT_API_URLS.logout, { method: "POST" });
      setUser(null);
      router.push("/auth/sign-in");
      router.refresh();
    } catch (error) {
      console.error("Logout error", error);
    }
  };

  const refreshSession = useCallback(async (override?: boolean) => {
    if (isRefreshingRef.current) {
      console.log("Refresh already in progress, skipping...");
      return;
    }

    isRefreshingRef.current = true;

    try {
      const refreshedToken = await authenticationApi.authenticationRefreshTokenPOST();

      const sessionResponse = await fetch(NEXT_API_URLS.session, {
        method: "POST",
        body: JSON.stringify(refreshedToken.body),
      });
      if (sessionResponse.ok) {
        const data = await sessionResponse.json();
        if (data.user) {
          setUser(data.user);
        }
      } else if (sessionResponse.status === 401) {
        // Session expired, log out user
        console.log("Session expired, logging out");
        setUser(null);
        router.push("/auth/sign-in");
      } else {
        console.error("Session refresh failed:", sessionResponse.status);
      }
    } catch (error) {
      console.error("Session refresh error:", error);
    }
    finally {
      isRefreshingRef.current = false;
    }
  }, [router]);

  const hasRole = (role: ERoles) => {
    if (!user?.roles.length) return false;
    return user.roles.map((r) => r.roleName).includes(role);
  };

  const isInRole = (role: ERoles) => {
    if (!user?.currentRole) return false;
    return user.currentRole.roleName === role;
  };

  const switchRole = async (roleId: string) => {
    try {
      setIsLoading(true);
      if (!user?.roles.length || !user.roles.find(r => r.id == roleId))
        return;
      var response = await roleManagementApi.roleManagementSwitchRolePOST({ roleId });

      // Create nextjs session from response
      const sessionResponse = await fetch(NEXT_API_URLS.session, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(response.body),
      });

      if (!sessionResponse.ok) {
        throw new Error("Failed to create session");
      }

      const data = (await sessionResponse.json()) as LoginResponseUserBody;
      setUser(data.user);
      const currentRoleName = data.user.currentRole.roleName as ERoles;

      window.location.href = roleHome[currentRoleName] ?? "/something-went-wrong";
    } catch (error: any) {
      console.error("Login error");
      throw new Error(error.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return <AuthContext.Provider value={{ user, isLoading, login, logout, hasRole, isInRole, switchRole, refreshSession }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Legacy hook for backward compatibility with existing code
export const useSession = () => {
  const { user, isLoading } = useAuth();

  return {
    data: user
      ? {
        user: {
          id: user?.id,
          email: user?.email,
          username: user?.userName,
          firstName: user?.firstName,
          lastName: user?.lastName,
          name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.userName,
          roles: user?.roles ?? [],
        },
      }
      : null,
    status: isLoading ? "loading" : user ? "authenticated" : "unauthenticated",
  };
};
